import { Alert, AlertColor, Portal, Snackbar } from "@mui/material";
import React, {
  PropsWithChildren,
  createContext,
  useContext,
  useReducer,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { Player } from "types/Player/type";

type Notification = {
  message: string;
  severity?: AlertColor;
};

interface INotificationContext {
  notifications: Notification[];
  dispatchNotification: (action: ActionNotification) => void;
}
export const NotificationContext = createContext<INotificationContext>({
  notifications: [],
  dispatchNotification: () => {},
});

const NotificationFC = () => {
  const { notifications, dispatchNotification } =
    useContext(NotificationContext);
  if (notifications.length) {
    const { message, severity = "error" } = notifications[0];

    return (
      <Portal>
        <Snackbar
          open
          autoHideDuration={4000}
          anchorOrigin={{ horizontal: "left", vertical: "bottom" }}
          onClose={() => dispatchNotification("pop")}
        >
          <Alert variant="filled" severity={severity} sx={{ width: "100%" }}>
            {message}
          </Alert>
        </Snackbar>
      </Portal>
    );
  } else return <></>;
};

type ActionNotification = "pop" | Notification;

const NotificationProvider = (props: PropsWithChildren) => {
  const [state, dispatchNotification] = useReducer(
    (state: INotificationContext, action: ActionNotification) => {
      if (action === "pop") {
        let notifications = [...state.notifications];
        notifications.shift();
        return {
          notifications,
          dispatchNotification: state.dispatchNotification,
        };
      } else {
        let notifications = [...state.notifications];
        notifications.push({ ...action });
        return {
          notifications,
          dispatchNotification: state.dispatchNotification,
        };
      }
    },
    {
      notifications: [],
      dispatchNotification: () => {},
    }
  );
  return (
    <NotificationContext.Provider value={{ ...state, dispatchNotification }}>
      <NotificationFC />
      {props.children}
    </NotificationContext.Provider>
  );
};

export const useErrorNotification = () => {
  const { dispatchNotification } = useContext(NotificationContext);
  const { t } = useTranslation();

  const dispatchErrorNotification = (error: string, payload?: any) => {
    let message: string = "";

    switch (error) {
      case "player-left":
        message = t("{{player}} s'est déconnecté", {
          player: (payload as Player).pseudo,
        });
        break;
      case "solo-room":
        message = t("Ce salon est une partie solo : personne ne peut le rejoindre");
        break;
      case "solo-only":
        message = t("Cette action n'est disponible qu'en mode solo");
        break;
      case "solo-captain-only":
        message = t("En solo, tu joues forcément un capitaine : J1 ou J2");
        break;
      default:
        message = error;
        break;
    }
    dispatchNotification({
      message,
    });
  };

  return dispatchErrorNotification;
};

export default NotificationProvider;
